/** Which axis the scroller windows. */
export type VirtualizeDirection = 'vertical' | 'horizontal'

/**
 * The two axes differ only in which properties name the scroll geometry, so
 * every axis-dependent read and every generated declaration resolves through
 * one record instead of branching at each site.
 */
export interface AxisGeometry {
	/** Scroll offset along the windowed axis. */
	readonly scroll: 'scrollTop' | 'scrollLeft'
	/** Viewport extent along the windowed axis. */
	readonly client: 'clientHeight' | 'clientWidth'
	/** The `overflow-*` longhand that makes an ancestor the scroller. */
	readonly overflow: 'overflowY' | 'overflowX'
	/** The dimension the strip and the spacer are sized along. */
	readonly size: 'height' | 'width'
	/** The dimension the strip stays 1px on. */
	readonly cross: 'width' | 'height'
	/** The computed gap a flow parent puts between the spacer and the window. */
	readonly gap: 'rowGap' | 'columnGap'
	/** The spacer's trailing margin along this axis. */
	readonly marginEnd: 'margin-block-end' | 'margin-inline-end'
	/**
	 * Spans the spacer across every cross-axis track of a grid scroller. Only the
	 * block axis carries one: an inline pin (`grid-row:1/-1`) changes nothing
	 * measurable on a rail, because a column-flow grid already gives the spacer
	 * its own column and a flex rail ignores grid placement outright.
	 */
	readonly pin?: 'grid-column'
}

export const AXIS: Record<VirtualizeDirection, AxisGeometry> = {
	vertical: {
		scroll: 'scrollTop',
		client: 'clientHeight',
		overflow: 'overflowY',
		size: 'height',
		cross: 'width',
		gap: 'rowGap',
		marginEnd: 'margin-block-end',
		pin: 'grid-column',
	},
	horizontal: {
		scroll: 'scrollLeft',
		client: 'clientWidth',
		overflow: 'overflowX',
		size: 'width',
		cross: 'height',
		gap: 'columnGap',
		marginEnd: 'margin-inline-end',
	},
}
